import { APIClient, AxiosAPIClient, Headers, Method, Request } from "@surya-digital/tedwig";
import { LeoUUID } from "@surya-digital/leo-ts-types";
import { AssetObjectType, FileType } from "./types";
import { assetObjects } from "./assetObjects";
import { AssetType } from "@pulse/pulse-rpcs/lib/file-service/assetType";
import { GetPresignedDownloadURLRPC } from "@pulse/pulse-rpcs/lib/file-service/getPresignedDownloadUrlRPC";
import { GetPresignedDownloadURLRPCClientImpl } from "@pulse/pulse-rpcs/lib/file-service/getPresignedDownloadUrlRPCClientImpl";
import { GetPresignedUploadURLRPC } from "@pulse/pulse-rpcs/lib/file-service/getPresignedUploadUrlRPC";
import { GetPresignedUploadURLRPCClientImpl } from "@pulse/pulse-rpcs/lib/file-service/getPresignedUploadUrlRPCClientImpl";
import { VerifyFileUploadRPC } from "@pulse/pulse-rpcs/lib/file-service/verifyFileUploadRPC";
import { VerifyFileUploadRPCClientImpl } from "@pulse/pulse-rpcs/lib/file-service/verifyFileUploadRPCClientImpl";
import { DeveloperError } from "./errors/DeveloperError";
import { FileServiceUnsupportedFileError } from "./errors/FileServiceUnsupportedFileError";
import { FileServiceMinSizeError } from "./errors/FileServiceMinSizeError";
import { FileServiceFileNameError } from "./errors/FileServiceFileNameError";
import { FileServiceMaxSizeError } from "./errors/FileServiceMaxSizeError";
import { FileServiceUploadError } from "./errors/FileServiceUploadError";
import { FileServiceInvalidFileIdError } from "./errors/FileServiceInvalidFileIdError";

export class FileService {
  private readonly client: APIClient;
  private readonly baseURL: string;

  /**
   * Constructs a new instance of the FileService class.
   * @param client - The API client for making requests to the file service.
   * @param baseURL - The base URL of the file service.
   */
  constructor(client: APIClient, baseURL: string) {
    this.client = client;
    this.baseURL = baseURL;
  }

  private readonly getPresignedUploadURLAndFileId = async (
    file: File,
    assetType: AssetType.AssetType,
    payload: string | null = null
  ): Promise<{
    presignedUploadUrl: URL;
    fileId: LeoUUID;
  }> => {
    try {
      const request = new GetPresignedUploadURLRPC.Request(file.name, file.size, assetType, payload);
      const getPresignedUploadUrlRPC = new GetPresignedUploadURLRPCClientImpl(this.client);
      const { response, error }: {
        response?: GetPresignedUploadURLRPC.Response,
        error?: GetPresignedUploadURLRPC.Errors.Errors,
      } = await getPresignedUploadUrlRPC.execute(request);
      if (response) {
        return Promise.resolve({ presignedUploadUrl: response.presignedUploadURL, fileId: response.fileId });
      } else {
        switch (error?.code) {
          case GetPresignedUploadURLRPC.RPCError.InvalidFileType: {
            return Promise.reject(new FileServiceUnsupportedFileError("This file type is not supported."));
          }
          case GetPresignedUploadURLRPC.RPCError.InvalidFileSize: {
            // RPC is applying validation directly on the file-size provided explicitly.
            // Hence this is a developer error as there shouldn't be any discrepancy between the file size validation of FileService and RPC.
            return Promise.reject(new DeveloperError("File size validation failed."));
          }
          default: {
            return Promise.reject(new DeveloperError("Unknown error."));
          }
        }
      }
    } catch (error) {
      if (error instanceof GetPresignedUploadURLRPC.InvalidRequestError) {
        return Promise.reject(new DeveloperError(`Upload request validation failed: ${error.message}`));
      } else {
        // This part of the code is not reachable. For sanity, rejecting the promise with the error as is.
        return Promise.reject(error);
      }
    }
  };

  private readonly uploadFile = async (
    presignedUploadURL: URL,
    file: File,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const uploadAxiosClient = new AxiosAPIClient({ timeoutMS: 0 });
      const uploadHeaders: Headers = new Headers([
        {
          name: "Content-Type",
          value: "multipart/form-data",
        }
      ]);
      uploadAxiosClient.sendRequest(
        new Request(
          Method.Put,
          presignedUploadURL.href,
          uploadHeaders,
          undefined,
          file,
        )
      )
        .then(() => resolve())
        .catch((error: Error) => {
          reject(new FileServiceUploadError("File upload failed.", error));
        });
    });
  };

  private readonly verifyUpload = async (fileId: LeoUUID): Promise<void> => {
    try {
      const request = new VerifyFileUploadRPC.Request(fileId);
      const verifyFileUploadRPC = new VerifyFileUploadRPCClientImpl(this.client);
      const { response, error }: {
        response?: VerifyFileUploadRPC.Response,
        error?: VerifyFileUploadRPC.Errors.Errors
      } = await verifyFileUploadRPC.execute(request);
      if (response) {
        return;
      } else {
        switch (error?.code) {
          case VerifyFileUploadRPC.RPCError.FileIdNotFound: {
            return Promise.reject(new DeveloperError("Internal error. Uploaded file couldn't be verified."));
          }
          default: {
            return Promise.reject(new DeveloperError("Internal error."));
          }
        }
      }
    } catch (error) {
      if (error instanceof VerifyFileUploadRPC.InvalidRequestError) {
        return Promise.reject(new DeveloperError("Internal error."));
      } else {
        // This part of the code is not reachable. For sanity, rejecting the promise with the error as is.
        return Promise.reject(error);
      }
    }
  }

  private readonly validateFileSize = (file: File, fileType: FileType): void => {
    const { maxSize, minSize } = fileType.fileSize;
    if (file.size > maxSize) {
      throw new FileServiceMaxSizeError(`File size is too large. Maximum allowed size is ${maxSize} bytes.`, maxSize, file.size);
    }
    if (file.size < minSize) {
      throw new FileServiceMinSizeError(`File size is too small. Minimum allowed size is ${minSize} bytes.`, minSize, file.size);
    }
  }

  private readonly validateAndGetFileTypeFromFile = (file: File, assetObject: AssetObjectType): FileType => {
    const fileExtension = file.name.split(".").pop();
    const fileType = assetObject.allowedFileTypes.find(fileType => fileType.name === fileExtension);
    if (!fileType) {
      throw new FileServiceUnsupportedFileError(`File type "${fileExtension}" is not allowed.`, fileExtension);
    }
    return fileType;
  }

  private readonly validateFileName = (fileName: string): void => {
    const maxFileNameLength = 500;
    const minFileNameLength = 2;
    const fileNameLength = fileName.length;
    if (fileNameLength !== fileName.trim().length) {
      throw new FileServiceFileNameError(
        `File name: "${fileName}" has leading and trailing spaces.`,
      );
    }
    if (fileNameLength < 2) {
      throw new FileServiceFileNameError(
        `File name is too short. Length is ${fileNameLength}. Minimum allowed length is ${minFileNameLength}.`,
      );
    }
    if (fileName.length > maxFileNameLength) {
      throw new FileServiceFileNameError(
        `File name is too long. Length is ${fileNameLength}. Maximum allowed length is ${maxFileNameLength}.`,
      );
    }
    if (fileName.trim().length === 0) {
      throw new FileServiceFileNameError(
        "File name cannot be empty.",
      );
    }
  }

  private readonly validateUploadRequest = (file: File, assetType: AssetType.AssetType): void => {
    this.validateFileName(file.name);
    const fileType = this.validateAndGetFileTypeFromFile(file, assetObjects[assetType]);
    this.validateFileSize(file, fileType);
  }

  /**
   * Uploads a file to the file service.
   *
   * @param file - The file to be uploaded.
   * @param assetType - The type of the asset associated with the file.
   * @param payload - This optional parameter should contain stringified JSON which contains any additional data that is required for uploading the file.
   *
   * @returns A promise that resolves to the file ID of the uploaded file.
   *
   * @throws FileServiceUnsupportedFileError - If the file type is not supported.
   * @throws FileServiceMinSizeError - If the file size is too small.
   * @throws FileServiceMaxSizeError - If the file size is too large.
   * @throws FileServiceFileNameError - If the file name is invalid.
   * @throws FileServiceUploadError - If the file upload fails.
   * @throws DeveloperError - If an internal error occurs.
   */
  readonly upload = async (file: File, assetType: AssetType.AssetType, payload: string | null = null): Promise<LeoUUID> => {
    try {
      this.validateUploadRequest(file, assetType);
      // Promise chaining is not possible here because `getPresignedUploadURLAndFileId` returns a fileId that is not returned by verifyUpload but is needed to resolve this promise finally.
      const { presignedUploadUrl, fileId } = await this.getPresignedUploadURLAndFileId(file, assetType, payload);
      await this.uploadFile(presignedUploadUrl, file);
      await this.verifyUpload(fileId);
      return Promise.resolve(fileId);
    }
    catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Retrieves a presigned download URL for a file associated with the given file ID.
   *
   * @param fileId - The ID of the file for which to retrieve the presigned download URL.
   * @param payload - This optional parameter should contain stringified JSON which contains any additional data that is required for uploading the file.
   *
   * @returns A promise that resolves to the presigned download URL.
   *
   * @throws FileServiceInvalidFileIdError - If the file ID is not found.
   * @throws DeveloperError - If an internal error occurs.
   */
  readonly getPresignedDownloadURL = async (fileId: LeoUUID, payload: string | null = null): Promise<URL> => {
    try {
      const request = new GetPresignedDownloadURLRPC.Request(fileId, payload);
      const getPresignedDownloadURLRPC = new GetPresignedDownloadURLRPCClientImpl(this.client);
      const { response, error }: {
        response?: GetPresignedDownloadURLRPC.Response,
        error?: GetPresignedDownloadURLRPC.Errors.Errors
       } = await getPresignedDownloadURLRPC.execute(request);
      if (response) {
        return Promise.resolve(response.presignedDownloadURL);
      } else {
        switch (error?.code) {
          case GetPresignedDownloadURLRPC.RPCError.FileIdNotFound: {
            return Promise.reject(new FileServiceInvalidFileIdError(`File Id "${fileId}" not found.`));
          }
          default: {
            return Promise.reject(new DeveloperError("Internal error."));
          }
        }
      }
    } catch (error) {
      if (error instanceof GetPresignedDownloadURLRPC.InvalidRequestError) {
        return Promise.reject(new DeveloperError("Download request vallidation failed."));
      } else {
        // This part of the code is not reachable. For sanity, rejecting the promise with the error as is.
        return Promise.reject(error);
      }
    }
  }

  /**
   * Downloads a file from the provided URL.
   *
   * @param url - The URL from which to download the file.
   */
  readonly downloadFromURL = (url: URL): void => {
    const anchorElement = document.createElement("a");
    anchorElement.href = url.href;
    anchorElement.download = "";
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
  }

  /**
   * Downloads a file from the file service using the provided file ID.
   *
   * @param fileId - The ID of the file to be downloaded.
   * @param payload - This optional parameter should contain stringified JSON which contains any additional data that is required for uploading the file.
   *
   * @returns A promise that resolves when the file is downloaded successfully.
   *
   * @throws FileServiceInvalidFileIdError - If the file ID is not found.
   * @throws DeveloperError - If an internal error occurs.
   *
   * @remarks
   * This method first retrieves the presigned download URL for the given file ID using the `getPresignedDownloadURL` method.
   * Then, it uses the `downloadFromURL` method to download the file from the obtained URL.
   *
   * @example
   * ```typescript
   * const fileService = new FileService(client, baseURL);
   * const fileId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
   * const organizationId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
   * const projectId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
   * const payload = JSON.stringify({
   *   organizationId: organizationId, // The `organizationId` represents the organization's identifier required by the server for authorization purposes.
   *   projectId: projectId // The `projectId` represents the project's identifier required by the server for authorization purposes.
   * })
   * try {
   *   await fileService.downloadFromFileId(fileId, payload);
   *   console.log("File downloaded successfully.");
   * } catch (error) {
   *   console.error("Failed to download file:", error.message);
   * }
   * ```
   */
  readonly downloadFromFileId = async (fileId: LeoUUID, payload: string | null = null): Promise<void> => {
    try {
      const downloadURL = await this.getPresignedDownloadURL(fileId, payload);
      this.downloadFromURL(downloadURL);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Builds a static URL for a file associated with the given file ID.
   *
   * @param baseURL - The base URL of the file service.
   * @param fileId - The ID of the file for which to build the static URL.
   * @param payload - This optional parameter should contain stringified JSON which contains any additional data that is required for uploading the file.
   *
   * @returns A URL object representing the static URL for the file.
   *
   * @remarks
   * This function is used to construct a static URL for a file associated with the given file ID. This URL doesn't expire.
   *
   * @example
   * ```typescript
   * const baseURL = "https://example.com";
   * const fileId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
   * const organizationId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
   * const projectId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
   * const payload = JSON.stringify({
   *   organizationId: organizationId, // The `organizationId` represents the organization's identifier required by the server for authorization purposes.
   *   projectId: projectId // The `projectId` represents the project's identifier required by the server for authorization purposes.
   * })
   * const staticURL = getStaticURL(fileId, payload);
   * console.log(staticURL.href); // Output: "https://example.com/file-service/GetHttpPresignedDownloadURL?fileId=123e4567-e89b-12d3-a456-426614174000"
   * ```
   */
  readonly getStaticURL = (fileId: LeoUUID, payload: string | null = null): URL => {
    return buildStaticURL(this.baseURL, fileId, payload);
  }
}

// This function is outside of the FileService class, as it might be moved out of the scope of this file later.
const buildStaticURL = (baseURL: string, fileId: LeoUUID, payload: string | null = null): URL => {
  const staticURL = new URL(
    "file-service/GetHttpPresignedDownloadURL",
    baseURL,
  );
  staticURL.searchParams.set("fileId", fileId.uuid);
  if(payload) {
    staticURL.searchParams.set("payload", payload)
  }
  return staticURL;
}

enum GetPresignedUploadURLError {
  InvalidFileType = "INVALID_FILE_TYPE",
  InvalidFileSize = "INVALID_FILE_SIZE",
}

enum VerifyFileUploadError {
  FileIdNotFound = "FILE_ID_NOT_FOUND",
}

enum GetPresignedDownloadURLError {
  FileIdNotFound = "FILE_ID_NOT_FOUND",
}
